import { useEffect, useRef, useState } from "react";
import { MobileMultiSheet } from "@/components/apply/MobileMultiSheet";
import { Check, ChevronDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([tabindex="-1"]),textarea:not([disabled]),select:not([disabled]),[tabindex="0"]';

function focusAfter(el) {
  const all = [...document.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null && !n.closest("[data-radix-popper-content-wrapper]"));
  all[all.indexOf(el) + 1]?.focus();
}

const MOBILE_Q = "(max-width: 767px), (pointer: coarse) and (max-width: 1024px)";
function useIsMobile() {
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia(MOBILE_Q).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_Q);
    const on = () => setM(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return m;
}

export function MultiSelect({ id, options, value = [], onChange, placeholder, error, title = "Select", onDone }) {
  const [open, setOpen] = useState(false);
  const mobile = useIsMobile();
  const trigger = useRef(null);
  const tabOut = useRef(null);
  const toggle = (k) => onChange(value.includes(k) ? value.filter((v) => v !== k) : [...value, k]);
  const labelOf = (k) => (options.find(([o]) => o === k) || [k, k])[1];
  const onContentKey = (e) => {
    if (e.key !== "Tab") return;
    e.preventDefault();
    tabOut.current = e.shiftKey ? "back" : "next";
    setOpen(false);
  };
  const onCloseFocus = (e) => {
    const dir = tabOut.current;
    tabOut.current = null;
    if (dir !== "next") return;
    e.preventDefault();
    focusAfter(trigger.current);
  };

  const triggerBtn = (
    <button ref={trigger} type="button" id={id} data-testid={id} aria-expanded={open} aria-haspopup={mobile ? "dialog" : "listbox"} aria-labelledby={`${id}-label`}
      aria-describedby={value.length ? `${id}-tags` : undefined} onClick={mobile ? () => setOpen(true) : undefined}
      className={`w-full min-h-[48px] px-3 rounded-md border bg-white text-left text-base flex items-center justify-between gap-2 ${error ? "border-red-400" : "border-stone-300"}`}>
      <span className={`truncate ${value.length ? "text-stone-900" : "text-stone-400"}`}>
        {value.length ? `${value.length} selected: ${value.map(labelOf).join(", ")}` : placeholder}
      </span>
      <ChevronDown className="h-4 w-4 text-stone-400 shrink-0" />
    </button>
  );
  const tags = value.length > 0 && (
    <div data-testid={`${id}-tags`} className="mt-2 flex flex-wrap gap-1.5">
      {value.map((k) => (
        <span key={k} className="inline-flex items-center gap-1 pl-3 pr-1 h-9 rounded-full bg-amber-50 border border-amber-300 text-sm text-amber-900">
          {labelOf(k)}
          <button type="button" tabIndex={-1} aria-label={`Remove ${labelOf(k)}`} data-testid={`${id}-remove-${k}`} onClick={() => toggle(k)}
            className="h-7 w-7 rounded-full flex items-center justify-center hover:bg-amber-100">
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      ))}
    </div>
  );
  if (mobile) return (
    <div>
      {triggerBtn}
      {open && <MobileMultiSheet id={id} title={title} options={options} value={value} onToggle={toggle} onClose={() => { setOpen(false); onDone?.(); trigger.current?.focus({ preventScroll: true }); }} />}
      {tags}
    </div>
  );
  return (
    <div>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) onDone?.(); }}>
        <PopoverTrigger asChild>{triggerBtn}</PopoverTrigger>
        <PopoverContent align="start" onKeyDown={onContentKey} onCloseAutoFocus={onCloseFocus}
          className="p-0 w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)]">
          <Command>
            <CommandInput data-testid={`${id}-search`} placeholder="Search — arrows to move, Enter to select, Tab when done" className="text-base" />
            <CommandList className="max-h-[50vh]">
              <CommandEmpty>No match.</CommandEmpty>
              <CommandGroup>
                {options.map(([k, l]) => (
                  <CommandItem key={k} value={l} data-testid={`${id}-option-${k}`} onSelect={() => toggle(k)} className="min-h-[44px] text-base">
                    <span className={`mr-2 h-5 w-5 shrink-0 rounded border flex items-center justify-center ${value.includes(k) ? "bg-amber-800 border-amber-800 text-white" : "border-stone-300"}`}>
                      {value.includes(k) && <Check className="h-3.5 w-3.5" />}
                    </span>
                    {l}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className="border-t border-stone-200 p-2">
            <button type="button" data-testid={`${id}-done`} onClick={() => { setOpen(false); onDone?.(); }}
              className="w-full h-11 rounded-full bg-amber-800 text-white font-semibold">Done</button>
          </div>
        </PopoverContent>
      </Popover>
      {tags}
    </div>
  );
}
